<template>
  <section class="portside-section" v-if="blogs?.length">
    <div class="container">
      <div class="resources-badge">Resources</div>

      <h2 class="section-title">The Portside View</h2>
      <p class="section-subtitle">
        Stories and Trends from the World of International Trade
      </p>

      <!-- Cards Carousel -->
      <div class="cards-carousel" ref="carouselContainer">
        <!-- Left Arrow -->
        <button
          class="carousel-arrow left"
          @click="prevSlide"
          aria-label="Previous cards"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <!-- Carousel Track -->
        <div class="carousel-viewport">
          <div
            class="carousel-track"
            ref="carouselTrack"
            :style="{
              transform: `translateX(-${currentSlidePercentage}%)`,
              transition: isTransitioning
                ? 'transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                : 'none',
            }"
            @transitionend="handleTransitionEnd"
          >
            <!-- Prepend clones for infinite scroll -->
            <div
              v-for="(blog, index) in blogs?.slice(-cardsPerView)"
              :key="'clone-start-' + index"
              class="card"
              @click="redirectToBlog(blog)"
            >
              <img
                :src="$root.getCDNlink(blog?.bannerImage)"
                alt="Related Blog Banner"
                class="image"
                loading="lazy"
              />
              <div class="card-content">
                <h3 class="card-title">{{ blog?.title }}</h3>
                <p class="card-description">{{ blog?.summary }}</p>
                <div  class="card-cta" @click="redirectToBlog(blog)">
                  Read More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Actual blogs -->
            <div
              v-for="(blog, index) in blogs"
              :key="'blog-' + index"
              class="card"
              @click="redirectToBlog(blog)"
            >
              <img
                :src="$root.getCDNlink(blog?.bannerImage)"
                alt="Related Blog Banner"
                class="image"
                loading="lazy"
              />
              <div class="card-content">
                <h3 class="card-title">{{ blog?.title }}</h3>
                <p class="card-description">{{ blog?.summary }}</p>
                <div  class="card-cta" @click="redirectToBlog(blog)">
                  Read More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Append clones for infinite scroll -->
            <div
              v-for="(blog, index) in blogs?.slice(0, cardsPerView)"
              :key="'clone-end-' + index"
              class="card"
              @click="redirectToBlog(blog)"
            >
              <img
                :src="$root.getCDNlink(blog?.bannerImage)"
                alt="Related Blog Banner"
                class="image"
                loading="lazy"
              />
              <div class="card-content">
                <h3 class="card-title">{{ blog?.title }}</h3>
                <p class="card-description">{{ blog?.summary }}</p>
                <div  class="card-cta" @click="redirectToBlog(blog)">
                  Read More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Arrow -->
        <button
          class="carousel-arrow right"
          @click="nextSlide"
          aria-label="Next cards"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { GetBlog } from "@/api/APIs";

export default {
  name: "HomePageBlogsCarousel",
  data() {
    return {
      blogs: [],
      selectedCategory: "",
      currentSlide: 0,
      isTransitioning: false,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1200,
      autoplayInterval: null,
      touchStartX: 0,
      touchEndX: 0,
      isUserInteracting: false,
      transitionTimeout: null,
    };
  },
  computed: {
    cardsPerView() {
      if (this.windowWidth >= 1024) return 3;
      if (this.windowWidth >= 768) return 2;
      return 1;
    },
    currentSlidePercentage() {
      const cardWidth = 100 / this.cardsPerView;
      return cardWidth * this.currentSlide;
    },
    totalSlides() {
      return this.blogs.length + 2 * this.cardsPerView;
    },
  },
  methods: {
    async getBlogsList() {
      const payload = { category: this.selectedCategory };
      try {
        const response = await GetBlog(payload);
        if (response.message === "SUCCESS") {
          this.blogs = response.blog.slice(0, 8);
          // Initialize position after blogs are loaded
          this.$nextTick(() => {
            this.currentSlide = this.cardsPerView;
          });
        }
      } catch (error) {
        console.error("Error fetching blogs", error);
      }
    },

    redirectToBlog(blog) {
      this.$router.push(`/blog/${blog.slug}/${blog.id}`);
    },

    nextSlide() {
      if (this.isTransitioning) return;

      this.isUserInteracting = true;
      this.isTransitioning = true;
      this.currentSlide++;

      // Clear any existing timeout
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }

      // Set timeout to handle infinite loop after transition
      this.transitionTimeout = setTimeout(() => {
        this.handleInfiniteLoop();
      }, 650); // Slightly longer than transition duration
    },

    prevSlide() {
      if (this.isTransitioning) return;

      this.isUserInteracting = true;
      this.isTransitioning = true;
      this.currentSlide--;

      // Clear any existing timeout
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }

      // Set timeout to handle infinite loop after transition
      this.transitionTimeout = setTimeout(() => {
        this.handleInfiniteLoop();
      }, 650);
    },

    handleInfiniteLoop() {
      const totalBlogs = this.blogs.length;

      // If we're past the last real slide, jump to the beginning
      if (this.currentSlide >= totalBlogs + this.cardsPerView) {
        this.isTransitioning = false;
        this.currentSlide = this.cardsPerView;
      }

      // If we're before the first real slide, jump to the end
      else if (this.currentSlide < this.cardsPerView) {
        this.isTransitioning = false;
        this.currentSlide = totalBlogs + this.cardsPerView - 1;
      } else {
        this.isTransitioning = false;
      }

      // Reset user interaction flag after a delay
      setTimeout(() => {
        this.isUserInteracting = false;
      }, 100);
    },

    handleTransitionEnd() {
      // This ensures smooth transitions are completed
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
        this.handleInfiniteLoop();
      }
    },

    autoSlide() {
      if (!this.isUserInteracting && !this.isTransitioning) {
        this.isTransitioning = true;
        this.currentSlide++;

        // Handle infinite loop for auto-slide
        setTimeout(() => {
          this.handleInfiniteLoop();
        }, 650);
      }
    },

    handleResize() {
      const oldCardsPerView = this.cardsPerView;
      this.windowWidth = window.innerWidth;

      // Adjust current slide if cards per view changed
      if (oldCardsPerView !== this.cardsPerView) {
        const currentPage = Math.floor(
          (this.currentSlide - oldCardsPerView) / oldCardsPerView
        );
        this.currentSlide = this.cardsPerView + currentPage * this.cardsPerView;
      }
    },

    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.isUserInteracting = true;
      this.stopAutoplay();
    },

    handleTouchMove(e) {
      this.touchEndX = e.touches[0].clientX;
    },

    handleTouchEnd() {
      const diff = this.touchStartX - this.touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }

      // Resume autoplay after touch interaction
      setTimeout(() => {
        this.isUserInteracting = false;
        this.startAutoplay();
      }, 1000);
    },

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayInterval = setInterval(() => {
        this.autoSlide();
      }, 2000);
    },

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    },

    handleMouseEnter() {
      this.isUserInteracting = true;
      this.stopAutoplay();
    },

    handleMouseLeave() {
      this.isUserInteracting = false;
      this.startAutoplay();
    },
  },

  mounted() {
    this.getBlogsList();
    window.addEventListener("resize", this.handleResize);

    this.$nextTick(() => {
      const carousel = this.$refs.carouselContainer;
      if (carousel) {
        carousel.addEventListener("touchstart", this.handleTouchStart, {
          passive: true,
        });
        carousel.addEventListener("touchmove", this.handleTouchMove, {
          passive: true,
        });
        carousel.addEventListener("touchend", this.handleTouchEnd);
        carousel.addEventListener("mouseenter", this.handleMouseEnter);
        carousel.addEventListener("mouseleave", this.handleMouseLeave);
      }
    });

    // Start autoplay after component is fully mounted
    setTimeout(() => {
      this.startAutoplay();
    }, 1000);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.stopAutoplay();

    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
    }

    const carousel = this.$refs.carouselContainer;
    if (carousel) {
      carousel.removeEventListener("touchstart", this.handleTouchStart);
      carousel.removeEventListener("touchmove", this.handleTouchMove);
      carousel.removeEventListener("touchend", this.handleTouchEnd);
      carousel.removeEventListener("mouseenter", this.handleMouseEnter);
      carousel.removeEventListener("mouseleave", this.handleMouseLeave);
    }
  },
};
</script>

<style scoped>
.portside-section {
  width: 100%;
  position: relative;
  background-color: #fff3f7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 80px 140px;
  box-sizing: border-box;
  gap: 55px;
  text-align: center;
  font-size: 48px;
  color: #1c1c1c;
  font-family: Figtree;
}

.container {
  margin: 0;
  width: -webkit-fill-available;
}

.image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  transition: transform 0.3s ease;
}

.resources-badge {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background-color: #ffffff;
  color: #C94374;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #C94374;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
}

.section-title {
  color: #1e3a8a;
  font-size: 3rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 0.75rem;
}

.section-subtitle {
  color: #1c1c1c;
  font-size: 24px;
  text-align: center;
  margin: 0 0 3rem;
  font-style: normal;
}

/* Carousel */
.cards-carousel {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.carousel-viewport {
  flex: 1;
  overflow: hidden;
  border-radius: 1rem;
}

.carousel-track {
  display: flex;
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
}

/* Arrow Styling */
.carousel-arrow {
  background-color: #ffffff;
  border: 2px solid #e5e7eb;
  color: #1e3a8a;
  cursor: pointer;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.carousel-arrow:hover {
  background-color: #C94374;
  color: #ffffff;
  border-color: #C94374;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(253, 76, 140, 0.3);
}

.carousel-arrow:active {
  transform: scale(0.95);
}

.carousel-arrow.left {
  order: 1;
}

.carousel-arrow.right {
  order: 3;
}

.carousel-viewport {
  order: 2;
}

.card {
  background-color: #ffffff;
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  transform: translateZ(0);
}

.card:hover {
  transform: translateY(-4px) translateZ(0);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.card:hover .image {
  transform: scale(1.05);
}

/* Card sizing based on screen size */
@media (min-width: 1024px) {
  .card {
    width: calc(33.333% - 1rem);
    margin-right: 1rem;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .card {
    width: calc(50% - 1rem);
    margin-right: 1rem;
  }
}

@media (max-width: 767px) {
  .card {
    width: 100%;
    margin-right: 0;
            border: 1px solid #f1e1e6;
  }
}

.card-content {
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
}

.card-title {
  color: #1c1c1c;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-description {
  color: #6b7280;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1.5rem;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-cta {
  color: #1c1c1c;
  font-size: 0.9375rem;
  font-weight: 500;
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: color 0.3s ease;
  margin-top: auto;
}

.card-cta:hover {
  color: #C94374;
}

.card-cta svg {
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}

.card-cta:hover svg {
  transform: translateX(4px);
}

/* Responsive styles */
@media only screen and (min-width: 768px) and (max-width: 1023px) {
  .section-title {
    font-size: 2.5rem;
  }

  .section-subtitle {
    font-size: 1.125rem;
  }
}

@media (max-width: 767px) {
  .portside-section{
        padding: 45px 40px;
  }
  .section-title {
    font-size: 2rem;
  }

  .section-subtitle {
    font-size: 1rem;
    margin-bottom: 2rem;
  }

  .carousel-arrow {
    width: 40px;
    height: 40px;
    padding: 0.75rem;
  }

  .carousel-arrow svg {
    width: 20px;
    height: 20px;
  }
}


@media (max-width: 480px) {
  .portside-section{
    padding:25px;
  }

  .cards-carousel{
    gap: 5px;
  }
 .section-title{
  margin: 0 0 0.45rem;
  font-size: 36px;
 }
.section-subtitle{
  margin-bottom: 1.5rem;
}
  .resources-badge {
    padding: 0.375rem 1.25rem;
    margin-bottom: 12px;
  }
  .carousel-arrow{
width: 35px;
height: 35px;
  }
  .image{
    height: 200px;
            border-bottom: 1px solid #e5e2e2;
  }
.card{
          border: 1px solid #f1e1e6;
}
.card-content{
  padding: 0.75rem;
}
  .card-title {
    font-size: 1.125rem;
    margin-bottom: 0.3rem;
  }
.card-cta{
  font-size: 14px;
  color: #C94374;
}
  .card-description {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
}

/* Performance optimizations */
.carousel-track * {
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* Smooth scrolling for better performance */
@media (prefers-reduced-motion: no-preference) {
  .carousel-track {
    scroll-behavior: smooth;
  }
}
</style>
